import { Product, DayKey, Package } from "../types/product.types";
import { getISOWeek } from 'date-fns';
import { log } from "./logger";
// (int) The current year
export const THIS_YEAR = +(new Date().getFullYear());

// (int) The current month starting from 1 - 12
// 1 => January, 12 => December
export const THIS_MONTH = +(new Date().getMonth()) + 1;

export const normalizeDatesToISO = (dates: string[] = []) =>
    dates.map(normalizeDate);

// Week days names and shortnames
export const WEEK_DAYS = {
    Monday: "Po",
    Tuesday: "Út",
    Wednesday: "St",
    Thursday: "Čt",
    Friday: "Pá",
    Saturday: "So",
    Sunday: "Ne",
}
export const WEEK_DAY_FULL = [
    "Pondělí",
    "Úterý",
    "Středa",
    "Čtvrtek",
    "Pátek",
    "Sobota",
    "Neděle"
]
export const WEEK_DAYS_EN = {
    Monday: "Mo",
    Tuesday: "Tu",
    Wednesday: "We",
    Thursday: "Th",
    Friday: "Fr",
    Saturday: "Sa",
    Sunday: "Su",
}

// Calendar months names and short names
export const CALENDAR_MONTHS = {
    January: "Leden",
    February: "Únor",
    March: "Březen",
    April: "Duben",
    May: "Květen",
    June: "Červen",
    July: "Červenec",
    August: "Srpen",
    September: "Září",
    October: "Říjen",
    November: "Listopad",
    December: "Prosinec"
}
export const CALENDAR_MONTHS_EN = {
    January: "January",
    February: "February",
    March: "March",
    April: "April",
    May: "May",
    June: "June",
    July: "July",
    August: "August",
    September: "September",
    October: "October",
    November: "November",
    December: "December"
}

// Orders after this hour are treated as next day for date-based logic.
export const ORDER_CUTOFF_HOUR = 14;
export const CZECH_TIME_ZONE = "Europe/Prague";

const czechFormatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: CZECH_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
});

type CzechParts = {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
};

const getCzechParts = (now: Date): CzechParts => {
    const parts = czechFormatter.formatToParts(now);
    const lookup = parts.reduce<Record<string, string>>((acc, p) => {
        acc[p.type] = p.value;
        return acc;
    }, {});
    return {
        year: Number(lookup.year),
        month: Number(lookup.month),
        day: Number(lookup.day),
        hour: Number(lookup.hour),
        minute: Number(lookup.minute),
    };
};

export const getCzechNowLocalDate = (now: Date = new Date()): Date => {
    const { year, month, day, hour, minute } = getCzechParts(now);
    return new Date(year, month - 1, day, hour, minute, 0, 0);
};

const formatISOFromParts = (y: number, m: number, d: number): string => {
    const mm = String(m).padStart(2, "0");
    const dd = String(d).padStart(2, "0");
    return `${y}-${mm}-${dd}`;
};

const isoToUTC = (iso: string): number | null => {
    if (!isISODate(iso)) return null;
    const [y, m, d] = iso.split("-").map(Number);
    return Date.UTC(y, m - 1, d);
};

const addDaysToISO = (iso: string, days: number): string => {
    const utc = isoToUTC(iso);
    if (utc == null) return iso;
    const next = new Date(utc + days * 24 * 60 * 60 * 1000);
    return next.toISOString().slice(0, 10);
};

const getCzechISO = (now: Date): string => {
    const { year, month, day } = getCzechParts(now);
    return formatISOFromParts(year, month, day);
};

const isAfterDailyCutoff = (now: Date, cutoffHour: number = ORDER_CUTOFF_HOUR): boolean => {
    const { hour, minute } = getCzechParts(now);
    return hour * 60 + minute >= cutoffHour * 60;
};

export const getEffectiveTodayStart = (
    now: Date = new Date(),
    cutoffHour: number = ORDER_CUTOFF_HOUR
): Date => {
    const baseISO = getCzechISO(now);
    const effectiveISO = isAfterDailyCutoff(now, cutoffHour) ? addDaysToISO(baseISO, 1) : baseISO;
    const [y, m, d] = effectiveISO.split("-").map(Number);
    return new Date(y, m - 1, d);
};

export const getEffectiveTodayISO = (
    now: Date = new Date(),
    cutoffHour: number = ORDER_CUTOFF_HOUR
): string => {
    const baseISO = getCzechISO(now);
    return isAfterDailyCutoff(now, cutoffHour) ? addDaysToISO(baseISO, 1) : baseISO;
};

export const getEffectiveTomorrowStart = (
    now: Date = new Date(),
    cutoffHour: number = ORDER_CUTOFF_HOUR
): Date => {
    const iso = addDaysToISO(getEffectiveTodayISO(now, cutoffHour), 1);
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d);
};

export const getEffectiveTomorrowISO = (
    now: Date = new Date(),
    cutoffHour: number = ORDER_CUTOFF_HOUR
): string => addDaysToISO(getEffectiveTodayISO(now, cutoffHour), 1);

export const isISODate = (s: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(s);

export const isOnOrAfterEffectiveToday = (
    iso: string,
    now: Date = new Date(),
    cutoffHour: number = ORDER_CUTOFF_HOUR
): boolean => {
    const targetUTC = isoToUTC(iso);
    const effectiveUTC = isoToUTC(getEffectiveTodayISO(now, cutoffHour));
    if (targetUTC == null || effectiveUTC == null) return false;
    return targetUTC >= effectiveUTC;
};

export const isOnOrAfterEffectiveTomorrow = (
    iso: string,
    now: Date = new Date(),
    cutoffHour: number = ORDER_CUTOFF_HOUR
): boolean => {
    const targetUTC = isoToUTC(iso);
    const effectiveUTC = isoToUTC(getEffectiveTomorrowISO(now, cutoffHour));
    if (targetUTC == null || effectiveUTC == null) return false;
    return targetUTC >= effectiveUTC;
};

export const filterDatesOnOrAfterEffectiveToday = (
    dates: string[],
    now: Date = new Date(),
    cutoffHour: number = ORDER_CUTOFF_HOUR
): string[] => dates.filter((d) => isOnOrAfterEffectiveToday(d, now, cutoffHour));

export const filterDatesOnOrAfterEffectiveTomorrow = (
    dates: string[],
    now: Date = new Date(),
    cutoffHour: number = ORDER_CUTOFF_HOUR
): string[] => dates.filter((d) => isOnOrAfterEffectiveTomorrow(d, now, cutoffHour));

// Find if atleast one of the dates is older than tomorrow
export function isAnyDateOlderThanTomorrow(selectedDates: string[]): boolean {
    const tomorrowISO = getEffectiveTomorrowISO();
    const tomorrowUTC = isoToUTC(tomorrowISO);
    if (tomorrowUTC == null) return false;

    return selectedDates.some(dateStr => {
        // Expecting format "5. 12. 2025"
        const parts = dateStr.split(".").map(p => p.trim()).filter(p => p !== "");
        if (parts.length !== 3) return false;

        const [dayStr, monthStr, yearStr] = parts;
        const day = Number(dayStr);
        const month = Number(monthStr);
        const year = Number(yearStr);

        const dateUTC = Date.UTC(year, month - 1, day);
        if (Number.isNaN(dateUTC)) return false;

        // Older than effective tomorrow = strictly less than tomorrow's midnight
        return dateUTC < tomorrowUTC;
    });
}




export function getDayOfWeek(date: Date): string {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[date.getDay()];
    };

export function getOddEvenWeek(date: Date): string {
    return getISOWeek(date) % 2 === 0 ? 'even' : 'odd'
}

export const getDayWord = (lang: string, days: number) => {
    if(lang === "en"){
        if(days === 1){
            return "day";
        }else{
            return "days";
        }
    }else{
        if(days === 1){
            return "den";
        }else if(days > 1 && days < 5){
            return "dny"
        }else{
            return "dní";
        }
    }
}
export function isAvailableOn(product: Product | Package, day: DayKey): boolean {
  return product.weekday[day];
}

// Weeks displayed on calendar
export const CALENDAR_WEEKS = 6;

// Pads a string value with leading zeroes(0) until length is reached
// Example: zeroPad(5, 2) => "05"
export const zeroPad = (value: number, length: number) => {
    return `${value}`.padStart(length, '0');
}

// (int) Number days in a month for a given year from 28 - 31
export const getMonthDays = (month = THIS_MONTH, year = THIS_YEAR) => {
    const months30 = [4, 6, 9, 11];
    const leapYear = year % 4 === 0;
    if (month === 2) {
        return leapYear ? 29 : 28;
    } else if (months30.includes(month)) {
        return 30;
    } else {
        return 31;
    }
}

// (int) First day of the month for a given year from 1 - 7
// 1 => Sunday, 7 => Saturday
export const getMonthFirstDay = (month = THIS_MONTH, year = THIS_YEAR) => {
    // getDay(): Sunday=0 ... Saturday=6
    const day = new Date(`${year}-${zeroPad(month, 2)}-01`).getDay();
    return day === 0 ? 7 : day; // Sunday (0) becomes 7, Monday (1) stays 1

}

// Returns current day number and hour
export const getCurrentDayHours = () => {
    const now = new Date();
    const { year, month, day: dayNum, hour } = getCzechParts(now);
    const utcDay = new Date(Date.UTC(year, month - 1, dayNum)).getUTCDay();
    const day = utcDay === 0 ? 7 : utcDay; // Sunday=7
    const hours = hour.toString().padStart(2, '0');
    return {day, hours}
}

export const getDayHours = (date?: string) => {
    if (!date) return getCurrentDayHours();

    const iso = normalizeDate(date);
    if (isISODate(iso)) {
        const [y, m, d] = iso.split("-").map(Number);
        const utcDay = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
        const day = utcDay === 0 ? 7 : utcDay;
        return { day, hours: "00" };
    }

    // Fallback to local parsing if the format is unexpected
    const now = new Date(date);
    const day = now.getDay() === 0 ? 7 : now.getDay();
    const hours = now.getHours().toString().padStart(2, '0');
    return { day, hours };
}

// Finds is dates are far enough appart
export const isDateFarEnough = (gap: number, date: string): boolean => {
    if (!Number.isFinite(gap) || gap < 0) return false;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
    const todayUTC = isoToUTC(getEffectiveTodayISO());
    const targetUTC = isoToUTC(date);
    if (todayUTC == null || targetUTC == null) return false;
    const msPerDay = 24 * 60 * 60 * 1000;
    const diffDays = Math.floor((targetUTC - todayUTC) / msPerDay);
    return diffDays >= gap;
};

export const extractGapDays = (s?: string | null): number | null => {
    const part = s?.split(" ")[0];
    if (part == null) return null;
    const n = Number(part);
    return Number.isFinite(n) ? n : null;
};


// Get current hour
export const getCurrentHour = () => {
    const now = new Date();
    const { hour, minute } = getCzechParts(now);
    const hours = hour.toString().padStart(2, '0');   // "08", "23", etc.
    const minutes = minute.toString().padStart(2, '0'); // "01", "45", etc.
    const time = `${hours}:${minutes}`; // e.g. "08:45"
    return time
}

// Turn time into minutes
export const timeToMinutes = (time: string) => {
    const [ hours, minutes ] = time.split(":").map(Number);
    return hours * 60 + minutes;
}

// Convert time string to minutes, returns null for invalid input
export const toMinutes = (time: string | undefined | null): number | null => {
    if (!time) return null;
    const [h, m] = time.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
};

// Check if a time falls between two bounds (inclusive)
export const isTimeBetween = (time: string, from?: string | null, to?: string | null): boolean => {
    const tt = toMinutes(time);
    if (tt === null) return false;
    const f = toMinutes(from);
    const ttt = toMinutes(to);

    if (f !== null && tt < f) return false;
    if (ttt !== null && tt > ttt) return false;
    return true;
}; 


// Formas date for display purposees
export const formatDate = (dateString: string | null, year?:boolean): string => {
    const date = dateString ? new Date(dateString) : new Date();
    const day = date.getDate();
    const month = date.getMonth() + 1; 
    if(year){
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
    }
    return `${day}.${month}`;
}

// (bool) Checks if a value is a date - this is just a simple check
export const isDate = (date: any) => {
    const isDate = Object.prototype.toString.call(date) === '[object Date]';
    const isValidDate = date && !Number.isNaN(date.valueOf());
    return isDate && isValidDate;
}

// "10/9/2025" (D/M/YYYY) -> "2025-09-10"
export const slashDMYtoISO = (s: string): string => {
    const m = /^\s*(\d{1,2})\/(\d{1,2})\/(\d{4})\s*$/.exec(s);
    if (!m) throw new Error(`Invalid D/M/YYYY date: ${s}`);
    const d = Number(m[1]), mon = Number(m[2]), y = Number(m[3]);
    return `${y}-${String(mon).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};

// (bool) Checks if two date values are of the same month and year
export const isSameMonth = (date: Date, basedate: Date = new Date()) => {
    if (!(isDate(date) && isDate(basedate))) return false;
    const basedateMonth = +(basedate.getMonth()) + 1;
    const basedateYear = basedate.getFullYear();
    const dateMonth = +(date.getMonth()) + 1;
    const dateYear = date.getFullYear();
    return (+basedateMonth === +dateMonth) && (+basedateYear === +dateYear);
}

// (bool) Checks if two date values are the same day
export const isSameDay = (date: Date, basedate: Date = new Date()) => {
    if (!(isDate(date) && isDate(basedate))) return false;
    const basedateDate = basedate.getDate();
    const basedateMonth = +(basedate.getMonth()) + 1;
    const basedateYear = basedate.getFullYear();
    const dateDate = date.getDate();
    const dateMonth = +(date.getMonth()) + 1;
    const dateYear = date.getFullYear();
    return (+basedateDate === +dateDate) && (+basedateMonth === +dateMonth) && (+basedateYear === +dateYear);
}

// Parse date
export const parseDateFromAnyFormat = (dateStr: string) => {
    if (dateStr.includes("/")) {
        // Format: DD/MM/YYYY
        const [day, month, year] = dateStr.split("/").map(Number);
        return new Date(year, month - 1, day);
    }
    // Format: YYYY-MM-DD or valid ISO
    return new Date(dateStr);
};
export function formatDMY(date: Date) {
    const day = date.getDate();
    const month = date.getMonth() + 1; // JS months are 0-based
    const year = date.getFullYear();
    return `${day}. ${month}. ${year}`;
}



// Add this debug version of parseDMY to see what's happening
export function parseDMY(dateStr: string) {
    const parts = dateStr.split('.').map(p => parseInt(p.trim(), 10));
    if (parts.length !== 3) {
        return null;
    }
    const [day, month, year] = parts;
    const date = new Date(Date.UTC(year, month - 1, day));
    return date;
}

// (string) Formats the given date as YYYY-MM-DD
// Months and Days are zero padded
export const getDateISO = (date = new Date) => {
    if (!isDate(date)) return null;
    return [
        date.getUTCFullYear(),
        zeroPad(+date.getMonth() + 1, 2),
        zeroPad(+date.getDate(), 2)
    ].join('-');
}

// ({month, year}) Gets the month and year before the given month and year
// Example: getPreviousMonth(1, 2000) => {month: 12, year: 1999}
export const getPreviousMonth = (month: number, year: number) => {
    const prevMonth = (month > 1) ? month - 1 : 12;
    const prevMonthYear = (month > 1) ? year : year - 1;
    return { month: prevMonth, year: prevMonthYear };
}

// ({month, year}) Gets the month and year after the given month and year
// Example: getNextMonth(1, 2000) => {month: 2, year: 2000}
export const getNextMonth = (month: number, year: number) => {
    const nextMonth = (month < 12) ? month + 1 : 1;
    const nextMonthYear = (month < 12) ? year : year + 1;
    return { month: nextMonth, year: nextMonthYear };
}


export default (month = THIS_MONTH, year = THIS_YEAR) =>{
    // Get umber of days anf the first day
    const monthDays = getMonthDays(month, year);
    const monthFirstDay = getMonthFirstDay(month, year);

    // Number of dayd from previous and next month to display
    const daysFromPrevMonth = monthFirstDay - 1; // Always make sure the first row is 7 days
    const daysFromNextMonth = (CALENDAR_WEEKS * 7) - (daysFromPrevMonth + monthDays); // Makes sure theres 42 days displayed

    // Get the previous and next months and years
    const { month: prevMonth, year: prevMonthYear } = getPreviousMonth(month, year);
    const { month: nextMonth, year: nextMonthYear } = getNextMonth(month, year);
    // Get number of days in previous month
    const prevMonthDays = getMonthDays(prevMonth, prevMonthYear);



    // Build array of last month dates to display
    const prevMonthDates = [...new Array(daysFromPrevMonth)].map((n, index) => {
        // Example day = 0 + 1 + (31 - 2) = 30
        const day = index + 1 + (prevMonthDays - daysFromPrevMonth);
        return [ prevMonthYear, zeroPad(prevMonth, 2), zeroPad(day, 2) ];
    });
    // Build array of current month dates to display
    const thisMonthDates = [...new Array(monthDays)].map((n, index) => {
        const day = index + 1;
        return [year, zeroPad(month, 2), zeroPad(day, 2)];
    });
    // Build array of next month dates to display
    const nextMonthDates = [...new Array(daysFromNextMonth)].map((n, index) => {
        const day = index + 1;
        return [nextMonthYear, zeroPad(nextMonth, 2), zeroPad(day, 2)];
    });
    // Combines all dates from previous, current and next months
    return [ ...prevMonthDates, ...thisMonthDates, ...nextMonthDates ];
}
/*
// Generate list of time with given intervals
export function generateTimes(interval = 15, start = 0, end=24){
    const times = []
    for (let hour = start; hour < end; hour++) {
        let endTime = 60;
        if(hour == end-1){
            endTime = 30;
        }
        for (let minutes = 0; minutes < endTime; minutes+= interval) {
            const h = hour.toString();
            const m = minutes.toString().padStart(2, "0");
            times.push(`${h}:${m}`)
        }
    }
    return times
}
*/

// Generate list of times with given intervals between start and end ("HH:MM").
// This version has endtime: 14:00 then last element is 13:15-14:00
/*
export function generateTimes(interval = 15, start = "0:00", end = "24:00") {

    const parseHM = (str: string): number => {
        const m = /^(\d{1,2}):(\d{2})$/.exec(str);
        if (!m) throw new Error(`Invalid time format: ${str}`);
        let h: number = Math.max(0, Math.min(24, parseInt(m[1], 10)));
        let min: number = Math.max(0, Math.min(59, parseInt(m[2], 10)));
        if (h === 24) min = 0; 
        return h * 60 + min;   
    };

    const roundUp = (mins: number, step: number): number => Math.ceil(mins / step) * step;
    const roundDown = (mins: number, step: number): number => Math.floor(mins / step) * step;

    const startMin = parseHM(start);
    const endMin = parseHM(end);

    const hardEnd = endMin - 45;
    if (hardEnd < 0) return [];

    // Align boundaries to the interval grid
    const first = roundUp(startMin, interval);
    const last = roundDown(hardEnd, interval);

    if (first > last) return [];

    const times = [];
    for (let t = first; t <= last; t += interval) {
        const h = Math.floor(t / 60);
        const m = (t % 60).toString().padStart(2, "0");
        times.push(`${h}:${m}`); 
    }

    return times;
}
*/

// Generate list of times with given intervals between start and end ("HH:MM").
// This version has endtime: 14:00 then last element is 14:00-14:45
export function generateTimes(
    interval: number = 15,
    start: string = "0:00",
    end: string = "24:00"
    ): string[] {

    // Parse times
    const parseHM = (str: string): number => {
        const m = /^(\d{1,2}):(\d{2})$/.exec(str);
        if (!m) throw new Error(`Invalid time format: ${str}`);
        let h = Math.max(0, Math.min(24, parseInt(m[1], 10)));
        let min = Math.max(0, Math.min(59, parseInt(m[2], 10)));
        if (h === 24) min = 0; // normalize "24:xx" -> "24:00"
        return h * 60 + min;   // minutes since midnight
    };
    // Format
    const formatTime = (mins: number): string => {
        const h = Math.floor(mins / 60);
        const mm = (mins % 60).toString().padStart(2, "0");
        return `${h}:${mm}`;
    };
    // Set min and max time
    const minTime = (n: number, step: number): number => Math.ceil(n / step) * step;
    const maxTime = (n: number, step: number): number => Math.floor(n / step) * step;

    // Validate interval
    if (!Number.isFinite(interval) || interval <= 0) {
        throw new Error(`interval must be a positive number. Got: ${interval}`);
    }
    // Start and end
    const startMin = parseHM(start);
    const endMin   = parseHM(end);

    // last valid start for a 45-min slot
    const lastStartMax = endMin - 45;
    const times: string[] = [];

    if (startMin <= lastStartMax) {
        const first = minTime(startMin, interval);
        const last  = maxTime(lastStartMax, interval);
        for (let t = first; t <= last; t += interval) {
        times.push(formatTime(t));
        }
    }
    // Always append the exact end time as the final element
    const endLabel = formatTime(endMin);
    if (times.length === 0 || times[times.length - 1] !== endLabel) {
        times.push(endLabel);
    }
    return times;
}

// Add minutes to given time
export function addMinutesToTime(timeStr: string, minutesToAdd: number) {
    // Split the string into and turn it into numbers
    const [hours, minutes] = timeStr.split(':').map(Number);
    // To minutes
    const totalMinutes = hours * 60 + minutes + minutesToAdd;
    // Get hours and minutes
    const newHours = Math.floor((totalMinutes % 1440) / 60); 
    const newMinutes = totalMinutes % 60;

    const hh = newHours.toString();
    const mm = newMinutes.toString().padStart(2, '0');
    return `${hh}:${mm}`;
}




































export function normalizeDate(dateStr: string): string {
  // Check if already in ISO-like format: YYYY-MM-DD
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(dateStr)) {
    // Already normalized (allowing single digit month/day)
    // Optionally pad month/day to 2 digits:
    const [year, month, day] = dateStr.split("-");
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  // Check if format is like "DD. M. YYYY" or "D. MM. YYYY"
  const parts = dateStr.split(".").map(p => p.trim());
  if (parts.length === 3) {
    const [day, month, year] = parts;
    if (/^\d+$/.test(day) && /^\d+$/.test(month) && /^\d{4}$/.test(year)) {
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }
  }

  // If none matched, return original string
  return dateStr;
}

export function denormalizeDate(dateStr: string): string {
  // Expecting dateStr like "2025-08-18" or "2025-8-1"
  const parts = dateStr.split("-");
  if (parts.length !== 3) return dateStr; // fallback if format unexpected

  const [year, month, day] = parts;
  // Remove leading zeros by converting to Number
  const d = Number(day);
  const m = Number(month);

  return `${d}. ${m}. ${year}`;
}
